import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiErr } from "@/lib/api";
import { ImageField, Field } from "./ImageField";

const EMPTY = { title: "", excerpt: "", date: new Date().toISOString().slice(0, 10), category: "", image: "", readTime: "5 min read", link: "" };

export const BlogTab = () => {
  const [posts, setPosts] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () =>
    api
      .get("/blog")
      .then((r) => setPosts(r.data))
      .catch((e) => toast.error(apiErr(e)));

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!editing.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      if (editing.id) await api.put(`/blog/${editing.id}`, editing);
      else await api.post("/blog", editing);
      toast.success(editing.id ? "Post updated" : "Post added");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/blog/${id}`);
      setPosts((prev) => prev.filter((p) => p.id !== id));
      setConfirmDelete(null);
      toast.success("Post deleted");
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  if (!posts) return <Loader2 className="mx-auto mt-20 h-6 w-6 animate-spin text-saffron" />;

  return (
    <div data-testid="admin-blog">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-ink">Blog Posts</h1>
          <p className="mt-1 font-body text-sm text-steel">{posts.length} posts · newest is shown as featured on the site</p>
        </div>
        <button
          data-testid="blog-add-btn"
          onClick={() => setEditing({ ...EMPTY })}
          className="inline-flex items-center gap-2 rounded-full bg-saffron px-5 py-2.5 font-heading text-sm font-semibold text-white transition-colors hover:bg-saffron-soft"
        >
          <Plus className="h-4 w-4" /> Add Post
        </button>
      </div>

      <div className="overflow-hidden rounded-xl border border-steel/20">
        {posts.map((p) => (
          <div key={p.id} data-testid={`blog-row-${p.id}`} className="flex items-center gap-4 border-b border-steel/15 bg-aero-surface px-4 py-3 last:border-0">
            <div className="grid h-12 w-16 shrink-0 place-items-center overflow-hidden rounded-md border border-steel/15 bg-white">
              {p.image && <img src={p.image} alt="" className="h-full w-full object-cover" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate font-heading text-sm font-semibold text-ink">{p.title}</p>
              <p className="font-body text-xs text-steel">
                {p.category} · {p.date}
              </p>
            </div>
            <button
              data-testid={`blog-edit-${p.id}`}
              onClick={() => setEditing({ ...EMPTY, ...p })}
              className="inline-flex items-center gap-1.5 rounded-full border border-steel/25 px-3 py-1.5 font-heading text-xs font-semibold text-steel transition-colors hover:border-saffron hover:text-saffron"
            >
              <Pencil className="h-3.5 w-3.5" /> Edit
            </button>
            {confirmDelete === p.id ? (
              <button
                data-testid={`blog-confirm-delete-${p.id}`}
                onClick={() => remove(p.id)}
                className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-3 py-1.5 font-heading text-xs font-semibold text-white"
              >
                <Trash2 className="h-3.5 w-3.5" /> Confirm?
              </button>
            ) : (
              <button
                data-testid={`blog-delete-${p.id}`}
                onClick={() => setConfirmDelete(p.id)}
                className="inline-flex items-center gap-1.5 rounded-full border border-red-200 px-3 py-1.5 font-heading text-xs font-semibold text-red-500 transition-colors hover:bg-red-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="blog-edit-dialog" className="max-h-[90vh] max-w-2xl overflow-y-auto border-steel/25 bg-aero-surface text-ink">
          {editing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl font-bold text-ink">
                  {editing.id ? "Edit Post" : "Add Post"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <Field label="Title" value={editing.title} testId="blog-field-title" onChange={(v) => setEditing({ ...editing, title: v })} />
                <Field label="Excerpt" textarea value={editing.excerpt} testId="blog-field-excerpt" onChange={(v) => setEditing({ ...editing, excerpt: v })} />
                <div className="grid gap-4 sm:grid-cols-3">
                  <Field label="Category" value={editing.category} testId="blog-field-category" onChange={(v) => setEditing({ ...editing, category: v })} />
                  <Field label="Date" type="date" value={editing.date} testId="blog-field-date" onChange={(v) => setEditing({ ...editing, date: v })} />
                  <Field label="Read time" value={editing.readTime} testId="blog-field-readtime" onChange={(v) => setEditing({ ...editing, readTime: v })} />
                </div>
                <ImageField label="Cover image" value={editing.image} testId="blog-image" onChange={(v) => setEditing({ ...editing, image: v })} />
                <Field label="External link (optional — opens the full article)" value={editing.link} testId="blog-field-link" onChange={(v) => setEditing({ ...editing, link: v })} />
                <button
                  data-testid="blog-save-btn"
                  onClick={save}
                  disabled={saving}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-saffron py-3 font-heading text-sm font-semibold text-white transition-colors hover:bg-saffron-soft disabled:opacity-60"
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editing.id ? "Save Changes" : "Add Post"}
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
